import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { Textarea } from "@/components/ui/textarea"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import {
  ArrowRight,
  Check,
  CheckCircle2,
  ExternalLink,
  Pencil,
  Send,
} from "lucide-react"
import { useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router"
import {
  useAcceptStale,
  useAnswerOverride,
  useCoverage,
  useGaps,
  useGapRefresh,
  useGapToMasterData,
  useMapping,
} from "../../../api/questionnaireProcessingApi"
import type { GapItemOut } from "../../../api/types/questionnaireProcessing"
import {
  normalizeFrameworkCode,
  regulationShortLabel,
} from "../../../utils/regulationLabel"
import { masterDataRecordRoute } from "../../master-data/masterDataAgentTargets"
import { FooterActionBar } from "../../shared/FooterActionBar"
import { McPill } from "../../shared/McPill"
import { ModuleHeader } from "../../shared/ModuleHeader"
import { customerRequestProcessPath } from "../routes"
import { FrameworkChips } from "./FrameworkChips"
import { FrameworkGroupedList } from "./FrameworkGroupedList"
import { ProcessingItemRow } from "./ProcessingItemRow"
import { useProcessingOutletContext } from "./QuestionnaireProcessingLayout"
import type { SheetCellClick } from "./SheetPreviewPanel"
import { SheetPreviewPanel } from "./SheetPreviewPanel"
import { useFrameworkGroups } from "./useFrameworkGroups"

// No cell-ref data on gap items (that lives in per-sheet column_indexes,
// keyed differently) — badge is a short framework label only. `canonicalId`
// also covers answering-service CATEGORIES that aren't a regulation at all
// (basic_info/lifecycle/mccp/weight) plus a PFAS sub-metric with no
// framework code of its own (lc_pfca) — `normalizeFrameworkCode` returns
// null for all five, so they keep their own short badge text here instead of
// routing through the shared regulation catalog. These abbreviations are not
// prose, so they read the same in every UI language and need no translate()
// key.
const NON_FRAMEWORK_SHORT_LABELS: Record<string, string> = {
  basic_info: "INFO",
  lifecycle: "LIFE",
  mccp: "MCCP",
  weight: "WT",
  lc_pfca: "PFCA",
}

function shortFrameworkLabel(
  gap: GapItemOut,
  translate: (key: TranslationKeys) => string,
): string {
  const code = normalizeFrameworkCode(gap.canonicalId)
  if (code) return regulationShortLabel(code, gap.canonicalId, translate)
  return NON_FRAMEWORK_SHORT_LABELS[gap.canonicalId] ?? gap.canonicalId
}

// gap.reasonCode -> translated copy. Mirrors `_gap_reason()` in
// mc_answering_service.py — a code missing here (a backend addition this
// catalog hasn't caught up with) falls back to `gap.reason` (English).
const GAP_REASON_KEYS: Partial<Record<string, TranslationKeys>> = {
  ANSWERED: "MC_QP_GAP_REASON_ANSWERED",
  MANUALLY_OVERRIDDEN: "MC_QP_GAP_REASON_MANUALLY_OVERRIDDEN",
  NOT_APPLICABLE: "MC_QP_GAP_REASON_NOT_APPLICABLE",
  CITED_DATA_EXPIRED: "MC_QP_GAP_REASON_CITED_DATA_EXPIRED",
  STALE_FRESHNESS_WINDOW: "MC_QP_GAP_REASON_STALE_FRESHNESS_WINDOW",
  STALE_FRESHNESS_WINDOW_UNKNOWN:
    "MC_QP_GAP_REASON_STALE_FRESHNESS_WINDOW_UNKNOWN",
  PENDING_REVIEW: "MC_QP_GAP_REASON_PENDING_REVIEW",
  MISSING_FMD: "MC_QP_GAP_REASON_MISSING_FMD",
  NO_DATA: "MC_QP_GAP_REASON_NO_DATA",
}

function gapReasonText(
  gap: GapItemOut,
  translate: (
    key: TranslationKeys,
    values?: Record<string, string | number>,
  ) => string,
): string {
  const key = GAP_REASON_KEYS[gap.reasonCode]
  return key ? translate(key, gap.reasonParams) : gap.reason
}

type GapKind = "stale" | "missing" | "answered"

// Kind used to be implicit in which of stale[]/missing[]/answered[] an item
// came from; now that the list is grouped by framework instead, derive it
// straight from answerStatus so each row still gets the right actions.
function kindOf(gap: GapItemOut): GapKind {
  if (gap.answerStatus === "stale") return "stale"
  if (gap.answerStatus === "answered" || gap.answerStatus === "overridden")
    return "answered"
  return "missing"
}

function GapCard({
  gap,
  kind,
  questionnaireId,
  isActive,
  onSelect,
}: {
  gap: GapItemOut
  kind: GapKind
  questionnaireId: string
  isActive: boolean
  onSelect: () => void
}) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const inquiryEnabled = useFeatureFlag("MC_SUPPLIER_STUDIO_INQUIRY")
  const supplierStudioEnabled = useFeatureFlag("MC_SUPPLIER_STUDIO")
  const { mutate: acceptStale, isPending: accepting } = useAcceptStale()
  const { mutate: toMasterData } = useGapToMasterData()
  const { mutate: requestRefresh, isPending: requesting } = useGapRefresh()
  const { mutate: override, isPending: saving } = useAnswerOverride()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState("")
  // The to-master-data endpoint is provisional bookkeeping only — GET /gaps
  // doesn't echo the request back, so track "already requested" locally for
  // this session instead of reading it off the gap itself.
  const [sentToMasterData, setSentToMasterData] = useState(false)
  // Same story for the supplier-inquiry refresh: bookkeeping only, not
  // echoed back by GET /gaps, so "already prepared" lives in local state.
  const [inquiryPrepared, setInquiryPrepared] = useState(false)

  return (
    <ProcessingItemRow
      articleNumber={gap.customerArticleNumber}
      isActive={isActive}
      onSelect={onSelect}
      topRight={
        <>
          {kind === "answered" && (
            <McPill tone="success">
              {translate("MC_QP_GAPS_ANSWERED_BADGE")}
            </McPill>
          )}
          <span className="font-mono shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] text-muted-foreground">
            {shortFrameworkLabel(gap, translate)}
          </span>
        </>
      }
    >
      <p
        className={cn(
          "mb-2 text-[12px]",
          kind === "stale"
            ? "text-warning"
            : kind === "answered"
              ? "font-medium text-success"
              : "text-red1",
        )}
      >
        {/* Answered items: the reason field is just a generic "answered"
            note — the value is what's actually useful to show here. */}
        {kind === "answered"
          ? (gap.value ?? gapReasonText(gap, translate))
          : gapReasonText(gap, translate)}
      </p>

      {sentToMasterData ? (
        <p className="flex items-center gap-1 text-[11px] text-success">
          <Check size={12} />
          {translate("MC_QP_GAPS_SENT_MASTER_DATA")}
        </p>
      ) : editing ? (
        <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="min-h-[64px] text-[12px]"
            autoFocus
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-7 bg-yellow1 px-2 text-[11px] text-black hover:bg-yellow2"
              disabled={saving}
              onClick={() =>
                override(
                  {
                    id: questionnaireId,
                    answerId: gap.answerId,
                    body: { value: draft },
                  },
                  { onSuccess: () => setEditing(false) },
                )
              }
            >
              {translate("MC_QP_GAPS_SAVE_VALUE")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-[11px]"
              onClick={() => setEditing(false)}
            >
              {translate("MC_QP_GAPS_CANCEL")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {kind === "stale" && (
            <Button
              size="sm"
              className="h-7 bg-yellow1 px-2 text-[11px] text-black hover:bg-yellow2"
              disabled={accepting}
              onClick={(e) => {
                e.stopPropagation()
                onSelect()
                acceptStale({ id: questionnaireId, answerId: gap.answerId })
              }}
            >
              <Check size={12} className="mr-1" />
              {translate("MC_QP_GAPS_OVERRIDE_BUTTON")}
            </Button>
          )}
          {kind === "missing" && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[11px]"
                onClick={(e) => {
                  e.stopPropagation()
                  onSelect()
                  setDraft("")
                  setEditing(true)
                }}
              >
                <Pencil size={12} className="mr-1" />
                {translate("MC_QP_GAPS_ENTER_MANUALLY")}
              </Button>
              {gap.articleId &&
                gap.supplierId &&
                inquiryEnabled &&
                supplierStudioEnabled && (
                  <PermissionGate permission={PERMISSIONS.MC_INQUIRIES_VIEW}>
                    <PermissionGate
                      permission={PERMISSIONS.MC_QUESTIONNAIRES_PROCESS}
                    >
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[11px]"
                        disabled={requesting || inquiryPrepared}
                        onClick={(e) => {
                          e.stopPropagation()
                          onSelect()
                          requestRefresh(
                            { id: questionnaireId, itemId: gap.itemId },
                            { onSuccess: () => setInquiryPrepared(true) },
                          )
                          const params = new URLSearchParams()
                          params.append("supplierIds", gap.supplierId!)
                          params.append("articleIds", gap.articleId!)
                          navigate(
                            `/material-compliance/supplier-studio/inquiry/new?${params.toString()}`,
                          )
                        }}
                      >
                        <Send size={12} className="mr-1" />
                        {translate(
                          inquiryPrepared
                            ? "MC_QP_GAPS_REQUEST_SUPPLIER_SENT"
                            : "MC_QP_GAPS_REQUEST_SUPPLIER",
                        )}
                      </Button>
                    </PermissionGate>
                  </PermissionGate>
                )}
              {gap.articleId && !gap.supplierId && (
                <>
                  <span className="self-center text-[11px] text-grey5">
                    {translate("MC_QP_GAPS_NO_SUPPLIER_HINT")}
                  </span>
                  <PermissionGate
                    permission={PERMISSIONS.MC_QUESTIONNAIRES_PROCESS}
                  >
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px]"
                      onClick={(e) => {
                        e.stopPropagation()
                        toMasterData(
                          { id: questionnaireId, itemId: gap.itemId },
                          { onSuccess: () => setSentToMasterData(true) },
                        )
                        navigate(
                          masterDataRecordRoute("article", gap.articleId!),
                        )
                      }}
                    >
                      <ExternalLink size={12} className="mr-1" />
                      {translate("MC_QP_GAPS_ASSIGN_SUPPLIER")}
                    </Button>
                  </PermissionGate>
                </>
              )}
              {!gap.articleId && (
                <>
                  <span className="self-center text-[11px] text-grey5">
                    {translate("MC_QP_GAPS_UNMAPPED_HINT")}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[11px]"
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(customerRequestProcessPath(questionnaireId, "reconcile"))
                    }}
                  >
                    <ExternalLink size={12} className="mr-1" />
                    {translate("MC_QP_GAPS_MAP_ARTICLE")}
                  </Button>
                </>
              )}
              {gap.articleId && gap.supplierId && (
                <PermissionGate
                  permission={PERMISSIONS.MC_QUESTIONNAIRES_PROCESS}
                >
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[11px]"
                    onClick={(e) => {
                      e.stopPropagation()
                      toMasterData(
                        { id: questionnaireId, itemId: gap.itemId },
                        { onSuccess: () => setSentToMasterData(true) },
                      )
                      navigate(masterDataRecordRoute("article", gap.articleId!))
                    }}
                  >
                    <ExternalLink size={12} className="mr-1" />
                    {translate("MC_QP_GAPS_OPEN_MASTER_DATA")}
                  </Button>
                </PermissionGate>
              )}
            </>
          )}
          {kind === "answered" && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[11px]"
                onClick={(e) => {
                  e.stopPropagation()
                  onSelect()
                  setDraft(gap.value ?? "")
                  setEditing(true)
                }}
              >
                <Pencil size={12} className="mr-1" />
                {translate("MC_QP_GAPS_EDIT_VALUE")}
              </Button>
              {gap.articleId && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px]"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(masterDataRecordRoute("article", gap.articleId!))
                  }}
                >
                  <ExternalLink size={12} className="mr-1" />
                  {translate("MC_QP_GAPS_OPEN_MASTER_DATA")}
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </ProcessingItemRow>
  )
}

export function GapsStep() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const { questionnaireId } = useProcessingOutletContext()
  const { data, isLoading } = useGaps(questionnaireId)
  const { data: coverage } = useCoverage(questionnaireId)
  const { data: mapping } = useMapping(questionnaireId)
  const [activeGap, setActiveGap] = useState<GapItemOut | null>(null)

  // A gap's bucket (stale/missing/answered) changes as the user resolves it —
  // e.g. entering a value manually moves it from `missing` to `answered` on
  // the next refetch. Concatenating the buckets fresh each time would then
  // reorder that item to wherever its new bucket sits, making it visually
  // jump elsewhere in the list right after saving. Track each item's
  // first-seen position instead and always sort back to that, so resolving
  // a gap updates it in place rather than moving it.
  const orderRef = useRef<Map<string, number>>(new Map())
  const nextOrderRef = useRef(0)
  const allGaps = useMemo(() => {
    const combined = [
      ...(data?.stale ?? []),
      ...(data?.missing ?? []),
      ...(data?.answered ?? []),
    ]
    combined.forEach((gap) => {
      if (!orderRef.current.has(gap.answerId)) {
        orderRef.current.set(gap.answerId, nextOrderRef.current++)
      }
    })
    return [...combined].sort(
      (a, b) =>
        (orderRef.current.get(a.answerId) ?? 0) -
        (orderRef.current.get(b.answerId) ?? 0),
    )
  }, [data])

  const { groups, registerSection, jumpTo } = useFrameworkGroups(
    allGaps,
    (gap) => gap.framework,
    translate("MC_QP_FRAMEWORK_OTHER"),
  )

  // Each gap's canonical_id maps back to the exact source column the
  // customer's sheet used for that question — join through the mapping
  // step's columns[] to highlight the real cell, not just the row.
  const activeColumn = useMemo(() => {
    if (!activeGap || !mapping) return null
    return (
      mapping.columns.find((c) => c.canonicalId === activeGap.canonicalId) ??
      null
    )
  }, [activeGap, mapping])

  // Reverse of activeColumn: clicked cell's header -> canonical_id (via the
  // same mapping join) -> the matching gap card, so the preview can drive
  // the list too.
  const handleCellClick = (cell: SheetCellClick) => {
    // sourceSheet is nullable on mapped columns — don't require an exact
    // match against it, or every column with a null sourceSheet never joins.
    const column = mapping?.columns.find(
      (c) =>
        c.sourceColumn === cell.header &&
        (c.sourceSheet == null || c.sourceSheet === cell.sheetName),
    )
    if (!column?.canonicalId) return
    const match = allGaps.find(
      (g) =>
        g.canonicalId === column.canonicalId &&
        (cell.itemId
          ? g.itemId === cell.itemId
          : g.customerArticleNumber === cell.customerArticleNumber),
    )
    setActiveGap(match ?? null)
  }

  if (isLoading || !data) {
    return <div className="flex h-full items-center justify-center" />
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="px-6 pb-2 pt-1">
        <ModuleHeader
          title={translate("MC_QP_GAPS_TITLE")}
          helpArticle={{
            categoryId: "customer-studio",
            articleId: "customer.requests",
          }}
          subtitle={translate("MC_QP_GAPS_SUBTITLE")}
        />
      </div>

      <FrameworkChips groups={groups} onJump={jumpTo} />

      <div className="min-h-0 flex-1 px-6 pb-0 pt-2">
        <div className="h-full overflow-hidden rounded-lg border">
          <ResizablePanelGroup orientation="horizontal">
            <ResizablePanel defaultSize={42} minSize={28}>
              <div className="flex h-full min-h-0 flex-col overflow-y-auto">
                {/* Grouped by framework — same shell ReviewStep uses, so the
                    two screens read as one consistent list. */}
                <FrameworkGroupedList
                  groups={groups}
                  registerSection={registerSection}
                  keyOf={(gap) => gap.answerId}
                  topContent={
                    coverage && (
                      <div className="m-3 flex items-start gap-3 rounded-lg border border-success/25 bg-success/6 p-3.5">
                        <CheckCircle2
                          size={16}
                          className="mt-0.5 shrink-0 text-success"
                        />
                        <div>
                          <p className="text-[13px] font-medium text-foreground">
                            {translate("MC_QP_GAPS_ANSWERABLE_NOW", {
                              answered: coverage.answered,
                              total: coverage.total,
                            })}
                          </p>
                          <p className="text-[11.5px] text-muted-foreground">
                            {translate("MC_QP_GAPS_ANSWERABLE_SUBTITLE")}
                          </p>
                        </div>
                      </div>
                    )
                  }
                  renderItem={(gap) => (
                    <GapCard
                      gap={gap}
                      kind={kindOf(gap)}
                      questionnaireId={questionnaireId}
                      isActive={activeGap?.answerId === gap.answerId}
                      onSelect={() => setActiveGap(gap)}
                    />
                  )}
                />
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={58} minSize={30}>
              <SheetPreviewPanel
                questionnaireId={questionnaireId}
                activeSheetName={activeColumn?.sourceSheet ?? undefined}
                highlightArticleNumber={
                  activeGap?.customerArticleNumber ?? null
                }
                highlightHeader={activeColumn?.sourceColumn ?? null}
                onCellClick={handleCellClick}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </div>

      <FooterActionBar left={<p>{translate("MC_QP_GAPS_FOOTER_NOTE")}</p>}>
        <Button
          className="bg-yellow1 text-black hover:bg-yellow2"
          onClick={() =>
            navigate(customerRequestProcessPath(questionnaireId, "review"))
          }
        >
          {translate("MC_QP_GAPS_GENERATE_BUTTON")}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </FooterActionBar>
    </div>
  )
}
